import { useMutation, useQuery } from '@apollo/client/react';
import {
  STAFF_BILLING_OTHER_COUPONS,
  STAFF_BILLING_UPSERT_OTHER_COUPON,
  STAFF_BILLING_DELETE_OTHER_COUPON,
} from '@staff-app/graphql-client';

/**
 * One row of the ROOT-DISCOUNT coupon list — the coupons OptiHub → Early Promoters Coupon (ROOT
 * only) may attach to a customer.
 *
 * ⚠ NAMING: the operator-facing label is **"Root discount coupons"** (Products List §4) and
 * "the root-discount list" in every message. Every IDENTIFIER deliberately keeps `other` —
 * the table `billing_other_coupons`, the ops `staffBilling*OtherCoupon*`, the types
 * `OtherCoupon*`, the testids `hw-other-coupons-*`. The label was renamed on 2026-09-15 and
 * the identifiers were not, on purpose: renaming a testid or an op to match a heading breaks
 * the audit rows and the specs that reference them, and a heading is allowed to change again
 * tomorrow. Do not "finish" the rename.
 *
 * The sibling of `useStripeCoupons`, over a different table (`public.billing_other_coupons`,
 * migration 040) and a different set of rules. Note the one field that is ABSENT next to
 * `StripeCouponCatalogEntry`: there is no `requiresTaxExemptDoc`, because migration 040 has
 * no such column. This list carries no document gate, and cannot be made to.
 *
 * MAPPING ONLY, as its sibling is — no percent, no amount, no currency. What a coupon takes
 * off lives in Stripe, and a mirror here could only drift from the money a customer is
 * actually charged.
 */
export type OtherCouponCatalogEntry = {
  /** Primary key, slug-shaped (`^[A-Z0-9][A-Z0-9_-]{1,63}$`) — validated server-side. */
  couponKey: string;
  /** The Stripe coupon id. No prefix, unlike `prod_…` / `price_…`. */
  stripeCouponId: string;
  label: string;
  terms?: string | null;
  /** Inactive rows keep the audit trail but never reach the Early Promoters Coupon picker. */
  isActive: boolean;
  stripeCouponName?: string | null;
  /** When the id was last round-tripped against this environment's Stripe key. */
  stripeValidatedAt?: string | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
};

/**
 * The maintenance list, INCLUDING inactive rows — deactivating is how a coupon is retired
 * without losing the row that explains a discount some customer is still carrying. The
 * Early Promoters Coupon picker reads the separate active-only `staffBillingOtherCouponCatalog`.
 */
export function useOtherStripeCoupons() {
  return useQuery<{ staffBillingOtherCoupons: OtherCouponCatalogEntry[] }>(
    STAFF_BILLING_OTHER_COUPONS,
    { fetchPolicy: 'cache-and-network' },
  );
}

/**
 * Both mutations refresh the Early Promoters Coupon picker's catalog as well as this list, or the
 * tab keeps offering the pre-edit list from cache — a deactivated coupon would still be
 * grantable in an open tab.
 */
const OTHER_COUPON_REFETCH = {
  refetchQueries: ['staffBillingOtherCoupons', 'staffBillingOtherCouponCatalog'],
};

/**
 * Save is FAIL-CLOSED on the server: it round-trips the id against this environment's Stripe
 * key before persisting, so a typo or a live-mode id pasted into sandbox is refused at entry.
 *
 * It ALSO refuses a coupon already in the Billing Console's approved catalog. One coupon
 * belongs to one list — otherwise the same discount is grantable under two rule sets (that
 * one can demand a certificate and never replaces; this one does neither and may replace),
 * with nothing in either audit row saying which authorised the grant. Errors are surfaced
 * verbatim, because that message names the row to fix.
 */
export function useUpsertOtherCoupon() {
  return useMutation<
    { staffBillingUpsertOtherCoupon: OtherCouponCatalogEntry },
    {
      couponKey: string;
      stripeCouponId: string;
      label: string;
      terms?: string;
      isActive?: boolean;
    }
  >(STAFF_BILLING_UPSERT_OTHER_COUPON, OTHER_COUPON_REFETCH);
}

/**
 * Deletes the LIST row only. Nothing in Stripe is touched and no customer already carrying
 * the discount loses it — this only stops the coupon being offered again.
 */
export function useDeleteOtherCoupon() {
  return useMutation<{ staffBillingDeleteOtherCoupon: boolean }, { couponKey: string }>(
    STAFF_BILLING_DELETE_OTHER_COUPON,
    OTHER_COUPON_REFETCH,
  );
}
